import { useState } from "react";
import { Button } from "@quake/shared-ui/components/ui/button";
import { cn } from "@quake/shared-ui/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import PlusIcon from "lucide-react/dist/esm/icons/plus";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import { trpc } from "~/utils/trpc";
import {
  SupportEmptyState,
  SupportErrorState,
  SupportListSkeleton,
  SupportPageHeader,
  SupportPageShell,
  TicketRow,
} from "./-components/support-desk-ui";
import type { SupportStatusGroup } from "./-components/support-desk.types";

export const Route = createFileRoute("/_dashboard/$datasetSlug/support/")({
  component: MySupportPage,
});

const filters: Array<{ value: "all" | SupportStatusGroup; label: string }> = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "closed", label: "Closed" },
];

function MySupportPage() {
  const { datasetSlug } = Route.useParams();
  const [statusGroup, setStatusGroup] = useState<"all" | SupportStatusGroup>("all");
  useBreadCrumbs([{ title: "My support", link: `/${datasetSlug}/support` }]);

  const ticketsQuery = useQuery(
    trpc.supportDesk.listMine.queryOptions(
      {
        limit: 50,
        statusGroup: statusGroup === "all" ? undefined : statusGroup,
      },
      { refetchInterval: 30_000 },
    ),
  );
  const tickets = ticketsQuery.data;

  return (
    <SupportPageShell>
      <SupportPageHeader
        title="My support"
        description="Create requests and keep every reply in one place."
        datasetSlug={datasetSlug}
        action={
          <Button size="sm" className="w-full cursor-pointer sm:w-auto" asChild>
            <Link to="/$datasetSlug/support/new" params={{ datasetSlug }}>
              <PlusIcon className="mr-1.5 h-4 w-4" />
              New request
            </Link>
          </Button>
        }
      />

      <div className="mt-4 overflow-hidden rounded-lg border border-border/80 bg-background">
        <div className="flex items-center gap-1 overflow-x-auto border-b border-border/70 bg-muted/15 px-2 py-2">
          {filters.map((filter) => (
            <button
              key={filter.value}
              type="button"
              className={cn(
                "cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                statusGroup === filter.value && "bg-background text-foreground shadow-sm ring-1 ring-border",
              )}
              onClick={() => setStatusGroup(filter.value)}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div className="hidden grid-cols-[7.5rem_minmax(0,1fr)_9rem_8rem] gap-4 border-b border-border/60 px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground sm:grid">
          <span>Ticket</span>
          <span>Request</span>
          <span>Status</span>
          <span className="text-right">Updated</span>
        </div>

        {ticketsQuery.isPending ? <SupportListSkeleton /> : null}
        {ticketsQuery.isError ? (
          <SupportErrorState message={ticketsQuery.error.message} onRetry={() => void ticketsQuery.refetch()} />
        ) : null}
        {ticketsQuery.isSuccess && tickets?.items.length === 0 ? <SupportEmptyState datasetSlug={datasetSlug} /> : null}
        {tickets?.items.map((ticket) => (
          <TicketRow key={ticket.id} ticket={ticket} datasetSlug={datasetSlug} />
        ))}
      </div>
    </SupportPageShell>
  );
}
